import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';

import { AppSettings } from '../../app.settings';
import { ApiService } from '../api/api.service';
import { ManagedFeatureFlagsService } from '../managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../session/session.service';
import { TeamCompanyUsersService } from '../teamcompanyusers/teamcompanyusers.service';
import { UsersService } from '../users/users.service';

export interface PayrollAccessState {
  canAccessPayrollAdmin: boolean;
  canShowPayrollAdminNav: boolean;
  isViewingPayrollAdmin: boolean;
  canCreatePayrollAdminManualMembers: boolean;
  canAccessPayrollExpenseApprovals: boolean;
  workspaceContext: PayrollWorkspaceContext;
  nativeCompanyManagerRoles: any[];
}

export type PayrollWorkspaceContext = 'none' | 'fullPayrollAdmin' | 'nativeCompanyManager';

const PAYROLL_ADMIN_NATIVE_ACCESS_FLAG = 'PAYROLL_ADMIN_WALLIT_NATIVE_ACCESS';
const PAYROLL_ADMIN_NATIVE_MANUAL_MEMBER_CREATION_FLAG = 'PAYROLL_ADMIN_WALLIT_NATIVE_MANUAL_MEMBER_CREATION';
const PAYROLL_ADMIN_EXPENSE_APPROVALS_FLAG = 'PAYROLL_ADMIN_EXPENSE_APPROVALS';

type PayrollAccessComputation = {
  canAccessPayrollAdmin: boolean;
  canShowPayrollAdminNav: boolean;
  canCreatePayrollAdminManualMembers: boolean;
  canAccessPayrollExpenseApprovals: boolean;
  workspaceContext: PayrollWorkspaceContext;
  nativeCompanyManagerRoles: any[];
};

@Injectable({
  providedIn: 'root',
})
export class PayrollAccessService {
  private readonly stateSubject = new BehaviorSubject<PayrollAccessState>({
    canAccessPayrollAdmin: false,
    canShowPayrollAdminNav: false,
    isViewingPayrollAdmin: false,
    canCreatePayrollAdminManualMembers: false,
    canAccessPayrollExpenseApprovals: false,
    workspaceContext: 'none',
    nativeCompanyManagerRoles: [],
  });
  private accessPromise: Promise<PayrollAccessComputation> | null = null;
  private cachedUserId = '';
  private cachedAccess: PayrollAccessComputation | null = null;
  private meWaitSubscription?: Subscription;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private sessionService: SessionService,
    private usersService: UsersService,
    private teamCompanyUsersService: TeamCompanyUsersService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncState();
      }
    });
  }

  get canAccessPayrollAdmin(): boolean {
    return this.stateSubject.value.canAccessPayrollAdmin;
  }

  get isViewingPayrollAdmin(): boolean {
    return this.stateSubject.value.isViewingPayrollAdmin;
  }

  get canCreatePayrollAdminManualMembers(): boolean {
    return this.stateSubject.value.canCreatePayrollAdminManualMembers;
  }

  get workspaceContext(): PayrollWorkspaceContext {
    return this.stateSubject.value.workspaceContext;
  }

  get nativeCompanyManagerRoles(): any[] {
    return [...this.stateSubject.value.nativeCompanyManagerRoles];
  }

  stateChanges(): BehaviorSubject<PayrollAccessState> {
    return this.stateSubject;
  }

  async refresh(forceRefresh = false): Promise<PayrollAccessState> {
    const access = await this.computeAccess(forceRefresh);
    return this.syncState(access);
  }

  private async computeAccess(forceRefresh = false): Promise<PayrollAccessComputation> {
    const userId = this.sessionService.getUserId();
    if (!userId || !this.apiService.isAuthenticated()) {
      this.cachedUserId = '';
      this.cachedAccess = {
        canAccessPayrollAdmin: false,
        canShowPayrollAdminNav: false,
        canCreatePayrollAdminManualMembers: false,
        canAccessPayrollExpenseApprovals: false,
        workspaceContext: 'none',
        nativeCompanyManagerRoles: [],
      };
      return this.cachedAccess;
    }

    if (!forceRefresh && this.cachedUserId === userId && this.cachedAccess) {
      return this.cachedAccess;
    }

    if (!forceRefresh && this.accessPromise) {
      return this.accessPromise;
    }

    this.accessPromise = (async () => {
      const me = this.usersService.me() || await this.waitForMe();
      const partnerId = `${me?.user?.partnerId || me?.brand?.partnerId || ''}`.trim() || undefined;
      if (this.sessionService.isSuperAdmin()) {
        const [canShowPayrollAdminNav, canAccessPayrollExpenseApprovals] = await Promise.all([
          this.managedFeatureFlagsService.isEnabled(
            PAYROLL_ADMIN_NATIVE_ACCESS_FLAG, 'team-admin', {userId, partnerId}, forceRefresh,
          ),
          this.managedFeatureFlagsService.isEnabled(
            PAYROLL_ADMIN_EXPENSE_APPROVALS_FLAG, 'team-admin', {userId, partnerId}, forceRefresh,
          ),
        ]);
        return {
          canAccessPayrollAdmin: true,
          canShowPayrollAdminNav,
          canCreatePayrollAdminManualMembers: true,
          canAccessPayrollExpenseApprovals,
          workspaceContext: 'fullPayrollAdmin',
          nativeCompanyManagerRoles: [],
        };
      }

      const rolesResponse = await this.teamCompanyUsersService.getManagers().catch(() => []);
      const roles = rolesResponse?.plain?.() || rolesResponse || [];
      const normalizedRoles = Array.isArray(roles) ? roles : [];
      const employerRoles = normalizedRoles.filter((role: any) => {
        return `${role?.type || ''}`.trim().toLowerCase() === 'employer';
      });
      const nativeCompanyManagerRoles = employerRoles.filter((role: any) => {
        return role?.role === 'manager' && !!`${role?.companyId || ''}`.trim();
      });
      const isPayrollAdminFirstTimeSetup = AppSettings.isPayrollAdmin() && normalizedRoles.length === 0;

      let canAccessPayrollAdmin = false;
      let workspaceContext: PayrollWorkspaceContext = 'none';
      let eligibleNativeCompanyManagerRoles: any[] = [];
      if (employerRoles.some((role: any) => role?.role === 'payrolladmin')) {
        canAccessPayrollAdmin = true;
        workspaceContext = 'fullPayrollAdmin';
      } else if (nativeCompanyManagerRoles.length) {
        const companyAccess = await Promise.all(nativeCompanyManagerRoles.map(async (role: any) => ({
          role,
          enabled: await this.managedFeatureFlagsService.isEnabled(
            PAYROLL_ADMIN_NATIVE_ACCESS_FLAG,
            'team-admin',
            {
              userId,
              partnerId,
              companyId: `${role.companyId}`.trim(),
            },
            forceRefresh,
          ),
        })));
        const enabledRoles = companyAccess.filter(result => result.enabled).map(result => result.role);
        canAccessPayrollAdmin = enabledRoles.length > 0;
        if (canAccessPayrollAdmin) {
          workspaceContext = AppSettings.isPayrollAdmin()
            ? 'fullPayrollAdmin'
            : 'nativeCompanyManager';
          eligibleNativeCompanyManagerRoles = workspaceContext === 'nativeCompanyManager'
            ? enabledRoles
            : [];
        }
      } else if (isPayrollAdminFirstTimeSetup) {
        // Allow payroll-admin-host users with no company roles yet to reach the setup flow.
        canAccessPayrollAdmin = true;
        workspaceContext = 'fullPayrollAdmin';
      }

      const isNativeCompanyManager = workspaceContext === 'nativeCompanyManager';
      const canCreatePayrollAdminManualMembers = isNativeCompanyManager
        || (canAccessPayrollAdmin
        ? await this.managedFeatureFlagsService.isEnabled(
          PAYROLL_ADMIN_NATIVE_MANUAL_MEMBER_CREATION_FLAG,
          'team-admin',
          {
            userId,
            partnerId,
          },
          forceRefresh,
        )
        : false);
      const canAccessPayrollExpenseApprovals = isNativeCompanyManager
        || (canAccessPayrollAdmin
        ? await this.managedFeatureFlagsService.isEnabled(
          PAYROLL_ADMIN_EXPENSE_APPROVALS_FLAG,
          'team-admin',
          {
            userId,
            partnerId,
          },
          forceRefresh,
        )
        : false);

      return {
        canAccessPayrollAdmin,
        canShowPayrollAdminNav: canAccessPayrollAdmin,
        canCreatePayrollAdminManualMembers,
        canAccessPayrollExpenseApprovals,
        workspaceContext,
        nativeCompanyManagerRoles: eligibleNativeCompanyManagerRoles,
      };
    })();

    try {
      const access = await this.accessPromise;
      this.cachedUserId = userId;
      this.cachedAccess = access;
      return access;
    } finally {
      this.accessPromise = null;
    }
  }

  private waitForMe(timeoutMs = 500): Promise<any> {
    const existing = this.usersService.me();
    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: any) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        this.meWaitSubscription?.unsubscribe();
        this.meWaitSubscription = undefined;
        resolve(value || null);
      };

      const timer = setTimeout(() => finish(this.usersService.me()), timeoutMs);
      this.meWaitSubscription = this.usersService.meSubscribe((me: any) => {
        if (me) {
          finish(me);
        }
      });
    });
  }

  private syncState(
    access: PayrollAccessComputation = {
      canAccessPayrollAdmin: this.stateSubject.value.canAccessPayrollAdmin,
      canShowPayrollAdminNav: this.stateSubject.value.canShowPayrollAdminNav,
      canCreatePayrollAdminManualMembers: this.stateSubject.value.canCreatePayrollAdminManualMembers,
      canAccessPayrollExpenseApprovals: this.stateSubject.value.canAccessPayrollExpenseApprovals,
      workspaceContext: this.stateSubject.value.workspaceContext,
      nativeCompanyManagerRoles: this.stateSubject.value.nativeCompanyManagerRoles,
    },
  ): PayrollAccessState {
    const isViewingPayrollAdmin = this.computeIsViewingPayrollAdmin(access.canAccessPayrollAdmin);
    const nextState = {
      canAccessPayrollAdmin: access.canAccessPayrollAdmin,
      canShowPayrollAdminNav: access.canShowPayrollAdminNav,
      isViewingPayrollAdmin,
      canCreatePayrollAdminManualMembers: access.canCreatePayrollAdminManualMembers,
      canAccessPayrollExpenseApprovals: access.canAccessPayrollExpenseApprovals,
      workspaceContext: access.workspaceContext,
      nativeCompanyManagerRoles: [...access.nativeCompanyManagerRoles],
    };
    this.stateSubject.next(nextState);
    return nextState;
  }

  private computeIsViewingPayrollAdmin(canAccessPayrollAdmin: boolean): boolean {
    const currentUrl = this.router.url || '';
    const routeIsPayrollAdmin = currentUrl.startsWith('/payrolladmin');
    const hostOrModeIsPayrollAdmin = AppSettings.isPayrollAdmin();
    return canAccessPayrollAdmin && (routeIsPayrollAdmin || hostOrModeIsPayrollAdmin);
  }
}
